import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { LuRefreshCw, LuServerCog, LuShieldCheck } from 'react-icons/lu';
import './RouteExperience.css';
import { isMarketingPublicPath } from '../seo/marketingSeo';
import { getSiteMode } from '../utils/siteHosts';
import { initAppClickTracker } from '../services/userTrackingService';
import { getApiBaseUrl } from '../services/api';
import {
  getServerMaintenanceState,
  SERVER_MAINTENANCE_EVENT,
  setServerMaintenanceActive,
} from '../services/serverMaintenance';

const ROUTE_LABELS = {
  '/': 'Unified Workforce',
  '/login': 'Sign in',
  '/signup': 'Create organization',
  '/user-signup': 'Join organization',
  '/forgot-password': 'Forgot password',
  '/reset-password': 'Reset password',
  '/overview': 'Overview',
  '/my-teams': 'My Team',
  '/screenshots': 'Screenshots',
  '/timelapse-videos': 'Timelapse Videos',
  '/timesheet': 'Timesheet',
  '/timeline': 'Timeline',
  '/manual-time': 'Manual Time',
  '/reports': 'Reports',
  '/payroll': 'Payroll',
  '/settings': 'Settings',
  '/billing': 'Billing',
  '/downloads': 'Downloads',
  '/account-settings': 'Account Settings',
  '/faq': 'FAQ',
  '/apply-leave': 'Apply Leave',
  '/leave-summary': 'Leave Summary',
  '/manage-leave': 'Manage Leave',
  '/manage-holiday': 'Holidays',
  '/risk-user-history': 'Risk Users',
  '/real-time-alert': 'Real Time Alerts',
  '/review-usage': 'Review Apps & Websites',
  '/apps-summary': 'Apps & Websites Summary',
  '/onboarding': 'Set up your workspace',
  '/onboarding/step-1': 'Set up your workspace',
  '/onboarding/step-2': 'Add your first teams',
  '/onboarding/step-3': 'Workspace setup complete',
  '/terms-of-service': 'Terms of Service',
  '/privacy-policy': 'Privacy Policy',
  '/blog': 'Blogs',
  '/view-image': 'View Image',
  '/application-logs': 'Application Logs',
  '/product-dashboard': 'Product Dashboard',
  '/founder-360': 'Founder 360° Intelligence',
  '/product-security': 'Product Security Monitoring',
  '/wiki': 'Documentation Wiki',
};

const humanize = (value) => value
  .replace(/^\/+|\/+$/g, '')
  .split('/')
  .filter(Boolean)
  .pop()
  ?.replace(/[-_]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Unified Workforce';

export const getRouteLabel = (pathname) => {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  if (pathname.startsWith('/user/')) return ROUTE_LABELS[pathname.replace('/user', '')] || humanize(pathname);
  if (pathname.startsWith('/reports/')) {
    const label = humanize(pathname);
    return /\bReport$/i.test(label) ? label : `${label} Report`;
  }
  if (pathname.startsWith('/settings/')) {
    const label = humanize(pathname);
    return /\bSettings$/i.test(label) ? label : `${label} Settings`;
  }
  if (pathname.startsWith('/payroll/')) return `Payroll ${humanize(pathname)}`;
  if (pathname.startsWith('/member/')) return 'Team Member';
  if (pathname.startsWith('/apps-history')) return 'Apps & Websites History';
  if (pathname.startsWith('/onboarding')) {
    if (pathname.endsWith('/step-2') || pathname.endsWith('/teams') || pathname.endsWith('/2')) return 'Add your first teams';
    if (pathname.endsWith('/step-3') || pathname.endsWith('/complete') || pathname.endsWith('/setup-complete') || pathname.endsWith('/3')) return 'Workspace setup complete';
    return 'Set up your workspace';
  }
  return humanize(pathname);
};

const RouteExperience = () => {
  const location = useLocation();
  const label = useMemo(() => getRouteLabel(location.pathname), [location.pathname]);
  const [maintenanceActive, setMaintenanceActive] = useState(() => getServerMaintenanceState().active);
  const [isOnline, setIsOnline] = useState(() => (
    typeof navigator !== 'undefined' ? navigator.onLine !== false : true
  ));

  useEffect(() => {
    const cleanup = initAppClickTracker();
    return () => cleanup && cleanup();
  }, []);

  useEffect(() => {
    const handleMaintenanceChange = (event) => {
      setMaintenanceActive(Boolean(event.detail?.active));
    };
    window.addEventListener(SERVER_MAINTENANCE_EVENT, handleMaintenanceChange);
    return () => window.removeEventListener(SERVER_MAINTENANCE_EVENT, handleMaintenanceChange);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setMaintenanceActive(false);
      setServerMaintenanceActive(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const siteMode = getSiteMode();
    if (siteMode === 'marketing' && isMarketingPublicPath(location.pathname)) {
      return undefined;
    }
    document.title = label === 'Unified Workforce' ? label : `${label} · Unified Workforce`;
    return undefined;
  }, [label, location.pathname]);

  const siteMode = getSiteMode();
  const showMaintenanceOverlay = maintenanceActive
    && isOnline
    && (typeof navigator === 'undefined' || navigator.onLine !== false)
    && siteMode !== 'marketing'
    && !isMarketingPublicPath(location.pathname);

  useEffect(() => {
    document.body.classList.toggle('server-maintenance-active', showMaintenanceOverlay);
    return () => document.body.classList.remove('server-maintenance-active');
  }, [showMaintenanceOverlay]);

  useEffect(() => {
    if (!showMaintenanceOverlay || typeof fetch !== 'function') return undefined;

    let cancelled = false;
    let timerId;
    const healthUrl = `${getApiBaseUrl().replace(/\/$/, '')}/health/`;

    const probeServer = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setServerMaintenanceActive(false);
        return;
      }
      fetch(healthUrl, { cache: 'no-store' })
        .then((response) => {
          if (!cancelled && response.ok) setServerMaintenanceActive(false);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) timerId = window.setTimeout(probeServer, 5000);
        });
    };

    timerId = window.setTimeout(probeServer, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [showMaintenanceOverlay]);

  return (
    <>
      {siteMode === 'app' ? (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      ) : null}
      <span className="route-experience-announcer" role="status" aria-live="polite" aria-atomic="true">
        {label} page loaded
      </span>
      {showMaintenanceOverlay ? (
        <div className="server-maintenance-overlay" role="presentation">
          <div
            className="server-maintenance-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="server-maintenance-title"
            aria-describedby="server-maintenance-description"
          >
            <div className="server-maintenance-icon" aria-hidden="true">
              <LuServerCog />
            </div>
            <div className="server-maintenance-copy">
              <p className="server-maintenance-kicker">Server update in progress</p>
              <h2 id="server-maintenance-title">Server under maintenance, please wait a minute.</h2>
              <p id="server-maintenance-description">
                Unified Workforce is deploying updates. This screen will automatically clear when the server is ready.
              </p>
            </div>
            <div className="server-maintenance-status">
              <span className="server-maintenance-spinner" aria-hidden="true">
                <LuRefreshCw />
              </span>
              <span>Reconnecting automatically</span>
            </div>
            <div className="server-maintenance-safe">
              <LuShieldCheck aria-hidden="true" />
              <span>Desktop activity stays safe and buffered locally, then syncs after reconnection.</span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default RouteExperience;
